import { useEffect, useRef } from 'react';
import { useWhatsappMessages, useWhatsappContact } from '../whatsappWebApi';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Search, MoreVertical, Info } from 'lucide-react';
import { MessageBubble } from './MessageBubble';
import { MessageComposer } from './MessageComposer';

interface ChatWindowProps {
  chatId: string;
}

export function ChatWindow({ chatId }: ChatWindowProps) {
  const { data: messages = [], isLoading } = useWhatsappMessages(chatId);
  const { data: contact } = useWhatsappContact(chatId);
  
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages.length, chatId]);

  const rawDigits = (contact?.number || chatId || '').replace(/\D/g, '');
  const formattedPhone = rawDigits ? `+${rawDigits}` : '';
  const displayName = contact?.name || contact?.pushname || formattedPhone || 'WhatsApp Chat';
  const initial = (displayName || '').replace(/^\+/, '').charAt(0).toUpperCase() || 'C';

  return (
    <div className="flex h-full flex-col w-full bg-[#efeae2] relative overflow-hidden">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.06] pointer-events-none z-0" 
        style={{ 
          backgroundImage: 'url("https://static.whatsapp.net/rsrc.php/v3/yl/r/r2qS65B_mE4.png")', 
          backgroundRepeat: 'repeat',
          backgroundSize: '400px'
        }} 
      />

      {/* Header */}
      <div className="flex h-[59px] shrink-0 items-center justify-between bg-[#f0f2f5] px-4 shadow-sm z-10 relative box-border">
        <div className="flex items-center gap-4 cursor-pointer">
          <div className="flex h-10 w-10 overflow-hidden items-center justify-center rounded-full bg-[#dfe5e7] text-[#54656f]">
            {contact?.picUrl ? (
              <img src={contact.picUrl} alt={displayName} className="h-full w-full object-cover" />
            ) : (
              <span className="text-body-lg font-medium">{initial}</span>
            )}
          </div>
          <div className="flex flex-col justify-center h-full">
            <span className="text-[16px] text-[#111b21] font-medium leading-tight">{displayName}</span>
            <span className="text-[12.5px] text-[#667781] leading-tight">
              {formattedPhone && displayName !== formattedPhone ? formattedPhone : 'WhatsApp Direct'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] hover:bg-black/5 transition-colors">
            <Search className="h-5 w-5" />
          </button>
          <button className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] hover:bg-black/5 transition-colors">
            <MoreVertical className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Message List */}
      <div className="flex-1 overflow-y-auto px-[5%] py-4 z-10 relative custom-scrollbar flex flex-col">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="rounded-full bg-white px-4 py-2 shadow-sm text-[#54656f] flex items-center gap-2 text-[14px]">
              <Spinner className="h-4 w-4 text-[#00a884]" /> Loading messages...
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center">
            <div className="rounded-[8px] bg-[#ffeecd] px-4 py-2 text-center text-[12px] text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,.13)] max-w-md">
              <Info className="mr-1 inline-block h-3 w-3 align-middle" aria-hidden />
              Messages sent and received here are logged in ZYNX's Communication Hub for your team's records.
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-1">
            <div className="mx-auto mb-6 mt-2 rounded-[8px] bg-[#ffeecd] px-[12px] py-[6px] text-center text-[12.5px] text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,.13)] max-w-[85%] sm:max-w-md inline-block self-center">
              <Info className="mr-1.5 inline-block h-3 w-3 align-middle" aria-hidden />
              Messages sent and received here are logged in ZYNX's Communication Hub for your team's records.
            </div>
            {messages.map((msg, i) => {
              // Group date headers could go here
              return <MessageBubble key={msg.id || i} msg={msg} />;
            })}
            <div ref={bottomRef} className="h-1 shrink-0" />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="z-10 relative bg-[#f0f2f5]">
        <MessageComposer chatId={chatId} />
      </div>
    </div>
  );
}
